import classNames from "classnames";
import { ReactNode } from "react";
import { NavLink } from "react-router-dom";

export interface RouteTabsProps {
  /** {@link RouteTab} elements. */
  children: ReactNode;
  /** Accessible name for the tab strip; pass a translated string. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Underlined tab strip whose tabs are routes, so each one is deep-linkable and
 * survives a refresh. Pair with a react-router `<Outlet />` rendering the
 * active child route. Reach for the `Tabs` component instead when the panels
 * are not routes.
 */
export function RouteTabs({ children, ariaLabel, className }: RouteTabsProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className={classNames("border-b border-gray-200", className)}
    >
      <div className="flex gap-4">{children}</div>
    </nav>
  );
}

export interface RouteTabProps {
  /** Route path relative to the layout route, e.g. `"billing"` or `""`. */
  to: string;
  /** Already-translated tab label. */
  label: ReactNode;
  /** Match the path exactly — required on the index tab so siblings don't activate it. */
  end?: boolean;
}

export function RouteTab({ to, label, end = false }: RouteTabProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        classNames(
          "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
          {
            "border-primary-500 text-primary-600": isActive,
            "border-transparent text-gray-600 hover:text-gray-900": !isActive,
          },
        )
      }
    >
      {label}
    </NavLink>
  );
}
